"use client";

import React, { useState, useEffect } from "react";
import { Link } from "@/components/common/Link";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { cn } from "@/lib/utils";
type FooterLanguage = "en" | "it";
type FooterMenuProps = {
  language: FooterLanguage;
};
function FooterMenu({
  language
}: FooterMenuProps) {
  useMenu();
  const t = {
    en: {
      home: "Home",
      details: "Wedding Day",
      travel: "Travel",
      registry: "Registry",
      rsvp: "RSVP",
      faq: "FAQ"
    },
    it: {
      home: "Home",
      details: "Matrimonio",
      travel: "Viaggio",
      registry: "Lista Nozze",
      rsvp: "RSVP",
      faq: "Domande"
    }
  }[language];
  const menuItems = [{
    href: "/",
    label: t.home
  }, {
    href: "/wedding-day/",
    label: t.details
  }, {
    href: "/travel/",
    label: t.travel
  }, {
    href: "/registry/",
    label: t.registry
  }, {
    href: "/rsvp/",
    label: t.rsvp
  }, {
    href: "/faq/",
    label: t.faq
  }];
  return <nav className="flex flex-wrap justify-center gap-x-8 gap-y-4">
      {menuItems.map((item, i) => <Link key={item.href} to={item.href} className="text-[10px] md:text-xs uppercase tracking-[0.2em] text-foreground/70 hover:text-primary transition-colors font-sans" data-index={i}>
          {item.label}
        </Link>)}
    </nav>;
}
export default function Footer() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll();
  const [language, setLanguage] = useState<FooterLanguage>("en");
  const currentYear = new Date().getFullYear();
  const businessName = "Sara & Michael";
  useEffect(() => {
    const savedLanguage = localStorage.getItem("site-language");
    if (savedLanguage === "en" || savedLanguage === "it") {
      setLanguage(savedLanguage);
    }
  }, []);
  useEffect(() => {
    const handleStorageChange = () => {
      const savedLanguage = localStorage.getItem("site-language");
      if (savedLanguage === "en" || savedLanguage === "it") {
        setLanguage(savedLanguage);
      }
    };
    window.addEventListener("storage", handleStorageChange);
    window.addEventListener("focus", handleStorageChange);
    return () => {
      window.removeEventListener("storage", handleStorageChange);
      window.removeEventListener("focus", handleStorageChange);
    };
  }, []);
  const t = {
    en: {
      heading: "Hope to See You in Rome!",
      rights: "All rights reserved.",
      createdBy: "Website created by Sara & Michael",
      playGame: "Click here to play a fun word game",
      hashtag: "#SaraandMichael2026"
    },
    it: {
      heading: "Ci Vediamo a Roma!",
      rights: "Tutti i diritti riservati.",
      createdBy: "Sito web creato da Sara e Michael",
      playGame: "Clicca qui per giocare a un divertente gioco di parole",
      hashtag: "#SaraandMichael2026"
    }
  }[language];
  return <section id="footer" className="relative bg-[#F6F1E9] text-foreground pt-24 pb-0 overflow-hidden">
      <div ref={ref} className={cn("relative z-10 container mx-auto px-4 flex flex-col items-center text-center transition-all duration-1000 ease-out", isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10")}>
        <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl text-foreground mb-12 italic">
          {t.heading}
        </h2>

        <div className="mb-24">
          <MenuProvider menu_id="2" className="footer-menu">
            <FooterMenu language={language} />
          </MenuProvider>
        </div>

        <div className="flex flex-col items-center gap-2 text-[10px] text-muted-foreground/60 font-sans mb-40 md:mb-56 uppercase tracking-widest">
          <p>{t.createdBy}</p>

         <a href="/wedding-day/#wedding-word-game" className="text-[10px] md:text-xs uppercase tracking-[0.2em] text-foreground/70 hover:text-primary transition-colors font-sans">
  {t.playGame}
</a>

          <div className="h-3"></div>

          <p>
            &copy; {currentYear} {businessName}. {t.rights}
          </p>

          <p>{t.hashtag}</p>
        </div>
      </div>

      {/* Floral line art */}
      <div className="pointer-events-none absolute bottom-0 left-0 w-full overflow-hidden opacity-90 md:opacity-100">
        <svg viewBox="0 0 1440 320" className="w-full h-[110px] sm:h-[140px] md:h-[180px] lg:h-[240px]" preserveAspectRatio="xMidYMax slice" aria-hidden="true">
          {/* soft base wash */}
          <path d="M0,320 L0,250 C80,235 120,210 170,195 C220,180 260,190 305,205 C345,218 385,235 430,230 C485,224 520,185 565,170 C610,155 650,165 700,190 C745,212 790,240 835,236 C890,231 930,190 975,176 C1020,162 1060,173 1105,193 C1150,214 1190,240 1240,238 C1300,235 1350,205 1440,180 L1440,320 Z" fill="#EADFD2" opacity="0.85" />

          {/* left floral stems */}
          <g stroke="#8A9A7B" strokeWidth="2.2" fill="none" opacity="0.9">
            <path d="M90 320 C95 275, 100 235, 92 180" />
            <path d="M130 320 C128 275, 135 240, 145 165" />
            <path d="M180 320 C185 280, 190 240, 182 185" />
            <path d="M92 225 C70 210, 62 190, 66 170" />
            <path d="M92 205 C115 192, 124 170, 120 145" />
            <path d="M145 210 C168 195, 178 170, 175 145" />
            <path d="M145 190 C125 176, 120 156, 124 136" />
            <path d="M182 230 C205 215, 215 190, 212 165" />
          </g>

          {/* left blossoms */}
          <g fill="#74161A" opacity="0.8">
            <circle cx="66" cy="170" r="6" />
            <circle cx="120" cy="145" r="6" />
            <circle cx="175" cy="145" r="6" />
            <circle cx="212" cy="165" r="6" />
          </g>

          <g fill="#D8A39D" opacity="0.75">
            <circle cx="92" cy="180" r="9" />
            <circle cx="145" cy="165" r="9" />
            <circle cx="182" cy="185" r="9" />
          </g>

          {/* center subtle florals */}
          <g stroke="#8A9A7B" strokeWidth="1.8" fill="none" opacity="0.55">
            <path d="M650 320 C648 280, 655 250, 662 210" />
            <path d="M720 320 C724 285, 730 250, 728 215" />
            <path d="M790 320 C788 285, 794 250, 804 220" />
            <path d="M662 240 C645 228, 638 210, 640 192" />
            <path d="M728 242 C746 228, 754 210, 752 190" />
            <path d="M804 248 C821 235, 830 215, 828 194" />
          </g>

          <g fill="#C97E48" opacity="0.5">
            <circle cx="640" cy="192" r="5" />
            <circle cx="752" cy="190" r="5" />
            <circle cx="828" cy="194" r="5" />
          </g>

          {/* right floral stems */}
          <g stroke="#8A9A7B" strokeWidth="2.2" fill="none" opacity="0.9">
            <path d="M1260 320 C1258 280, 1265 245, 1272 180" />
            <path d="M1310 320 C1315 275, 1320 235, 1312 165" />
            <path d="M1360 320 C1358 278, 1365 240, 1378 190" />
            <path d="M1272 215 C1248 198, 1240 178, 1244 156" />
            <path d="M1272 195 C1296 180, 1304 158, 1300 135" />
            <path d="M1312 195 C1335 180, 1342 160, 1338 137" />
            <path d="M1378 220 C1400 205, 1408 184, 1405 162" />
          </g>

          {/* right blossoms */}
          <g fill="#74161A" opacity="0.8">
            <circle cx="1244" cy="156" r="6" />
            <circle cx="1300" cy="135" r="6" />
            <circle cx="1338" cy="137" r="6" />
            <circle cx="1405" cy="162" r="6" />
          </g>

          <g fill="#D8A39D" opacity="0.75">
            <circle cx="1272" cy="180" r="9" />
            <circle cx="1312" cy="165" r="9" />
            <circle cx="1378" cy="190" r="9" />
          </g>
        </svg>
      </div>
    </section>;
}